import { useMemo } from "react";
import { useBoxWidth, linePath, niceScale, padRange, pctTick, shortDate, money, signed } from "./srUtil";

const ts = (iso) => Date.parse(`${iso}T00:00:00Z`);
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function quarterLabels(dates, x, R) {
  const out = [];
  let prev = null;
  dates.forEach((d, i) => {
    const m = +d.slice(5, 7);
    if (m !== prev && (m - 1) % 3 === 0 && R - x(i) > 60) out.push([x(i), out.length ? MON[m - 1] : `${MON[m - 1]} ${d.slice(0, 4)}`]);
    prev = m;
  });
  return out;
}

function geometry(chart, L, R, pt, pb, at, ab) {
  const { dates, price, entry } = chart;
  const T = dates.map(ts), t0 = T[0], t1 = T[T.length - 1];
  const x = (i) => L + ((T[i] - t0) / (t1 - t0)) * (R - L);
  const dx = (iso) => L + ((ts(iso) - t0) / (t1 - t0)) * (R - L);
  const [pmin, pmax] = padRange(Math.min(...price, entry.p25), Math.max(...price, entry.p75), 0.06);
  const y = (v) => pb - ((v - pmin) / (pmax - pmin)) * (pb - pt);
  const a0 = dates.length - chart.alpha_median.length;
  const as = niceScale(...padRange(Math.min(0, ...chart.alpha_median), Math.max(0, ...chart.alpha_median)), 4);
  const ay = (v) => ab - ((v - as.min) / (as.max - as.min)) * (ab - at);
  const ax = (i) => x(i + a0);
  const last = price[price.length - 1], py = y(last);
  let ey = y(entry.median);
  if (Math.abs(ey - py) < 34) ey = ey >= py ? py + 34 : py - 34;
  return { dx, y, ay, as, last, py, ey, ps: niceScale(pmin, pmax, 4), zero: ay(0), labels: quarterLabels(dates, x, R),
    med: linePath(chart.alpha_median.map((v, i) => [ax(i), ay(v)])),
    price: linePath(price.map((v, i) => [x(i), y(v)])), endLabel: shortDate(dates[dates.length - 1]) };
}

// Price with estimated entry band, then alpha vs sector along every started entry date, one calendar axis.
export const SetupChart = ({ chart, quarterLabel }) => {
  const [ref, w] = useBoxWidth(1120);
  const mobile = w < 600;
  const W = Math.max(320, w), H = mobile ? 470 : 480, L = mobile ? 44 : 62, R = W - (mobile ? 79 : 136);
  const pt = 42, pb = 260, at = 332, ab = 432;
  const g = useMemo(() => geometry(chart, L, R, pt, pb, at, ab), [chart, L, R]);
  const { entry, alpha_now: an } = chart;
  const qx0 = g.dx(chart.q_start), qx1 = g.dx(chart.q_end), color = an >= 0 ? "#34d399" : "#f87171";
  const big = (m, d) => (mobile ? m : d);
  return (
    <div ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" data-testid="sr-setup-chart"
        aria-label="Stock price, the funds’ estimated buy-in price, and the gain against the sector since they bought">
        <defs>
          <clipPath id="srpos"><rect x={L} y={at - 20} width={R - L} height={Math.max(0, g.zero - at + 20)} /></clipPath>
          <clipPath id="srneg"><rect x={L} y={g.zero} width={R - L} height={Math.max(0, ab - g.zero + 20)} /></clipPath>
        </defs>
        <rect x={qx0} y={pt} width={qx1 - qx0} height={pb - pt} fill="#34d399" opacity=".045" />
        <text x={qx0 + 4} y="27" fill="#9aa99f" fontSize={big(10, 11)}>{`${quarterLabel}: funds buying`}</text>
        {g.ps.ticks.map((v) => (
          <g key={v}><line x1={L} x2={R} y1={g.y(v)} y2={g.y(v)} stroke="#222b35" />
            <text x={L - 8} y={g.y(v) + 4} textAnchor="end" fill="#8e9aa9" fontSize="10">{money(v)}</text></g>
        ))}
        <rect x={qx0} y={g.y(entry.p75)} width={R - qx0} height={Math.max(1, g.y(entry.p25) - g.y(entry.p75))} fill="#34d399" opacity=".13" />
        <line x1={qx0} x2={R} y1={g.y(entry.median)} y2={g.y(entry.median)} stroke="#34d399" strokeDasharray="5 4" />
        <text x={R + 10} y={g.ey - 3} fill="#34d399" fontSize={big(12, 17)} data-testid="sr-entry-label">{`~${money(entry.median)}`}</text>
        <text x={R + 10} y={g.ey + 14} fill="#9fb3aa" fontSize="10">Funds’ buy-in</text>
        <path d={g.price} fill="none" stroke="#e6eaf0" strokeWidth="2.6" strokeLinejoin="round" />
        <circle cx={R} cy={g.py} r="4" fill="#e6eaf0" />
        <text x={R + 10} y={g.py} fill="#e6eaf0" fontSize={big(12, 19)}>{`$${g.last.toFixed(2)}`}</text>
        <text x={R + 10} y={g.py + 16} fill="#a0aab8" fontSize="10">Stock price</text>
        <line x1={L} x2={W - 20} y1="285" y2="285" stroke="#26303b" />
        <text x={L} y="310" fill="#c4ced9" fontSize={big(11, 13)}>Gain against the sector since funds bought (%)</text>
        {g.as.ticks.map((v) => (
          <g key={v}><line x1={L} x2={R} y1={g.ay(v)} y2={g.ay(v)} stroke={v === 0 ? "#657080" : "#222b35"} strokeDasharray={v === 0 ? "0" : "2 4"} />
            <text x={L - 8} y={g.ay(v) + 3} textAnchor="end" fill="#8e9aa9" fontSize="10">{pctTick(v)}</text></g>
        ))}
        <path d={g.med} stroke="#34d399" strokeWidth="2.5" fill="none" clipPath="url(#srpos)" />
        <path d={g.med} stroke="#f87171" strokeWidth="2.5" fill="none" clipPath="url(#srneg)" />
        <circle cx={R} cy={g.ay(an)} r="4" fill={color} />
        <text x={R + 10} y={g.ay(an)} fill={color} fontSize={big(13, 22)} data-testid="sr-alpha-now">{`${signed(an)}%`}</text>
        <text x={R + 10} y={g.ay(an) + 17} fill="#a0aab8" fontSize="10">vs sector</text>
        {g.labels.map(([lx, l], i) => <text key={i} x={lx} y="456" textAnchor="middle" fill="#8e9aa9" fontSize="10">{l}</text>)}
        <text x={R} y="456" textAnchor="middle" fill="#8e9aa9" fontSize="10">{g.endLabel}</text>
      </svg>
    </div>
  );
};
